import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { ShoppingBag, Menu, X, Heart, User, LogIn, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import CartDrawer from './CartDrawer';
import { motion, AnimatePresence } from 'motion/react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [location] = useLocation();
  const { totalItems } = useCart();
  const { user, loading, logout } = useAuth();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'All Spices', path: '/products' },
    { name: 'Pure Powders', path: '/products?filter=powder' },
    { name: 'Blended Masalas', path: '/products?filter=masala' },
    { name: 'Lookbook', path: '/brochure' },
    { name: 'Wholesale', path: '/wholesale' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-[#FAF7F2] border-b border-[#E5E0D8] shadow-sm transition-all duration-300 font-sans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            {/* Mobile Hamburger Button */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setIsOpen(true)}
                aria-label="Toggle navigation menu"
                className="w-9 h-9 rounded-full bg-[#EAE6DF] hover:bg-[#DDD8D0] flex items-center justify-center text-[#222222] transition-colors"
              >
                <Menu className="w-5 h-5 stroke-[2]" />
              </button>
            </div>

            {/* Brand Logo matching Priya Foods style */}
            <div className="flex-shrink-0 flex items-center">
              <Link href="/" className="flex flex-col items-center group cursor-pointer">
                <span className="font-serif font-extrabold text-xl sm:text-2xl tracking-tight text-[#1F1F1F] group-hover:text-[#D97736] transition-colors uppercase">
                  Ajjas <span className="text-[#C85A17]">Masala</span>
                </span>
                <span className="text-[7px] sm:text-[8px] font-bold uppercase tracking-[0.22em] text-[#C85A17] -mt-0.5 sm:-mt-1">
                  The Joy of Good Taste
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8 items-center">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  href={link.path}
                  className={`text-xs font-bold uppercase tracking-wider transition-colors relative py-1 hover:text-[#D97736] ${
                    location === link.path ? 'text-[#D97736]' : 'text-[#333333]'
                  }`}
                >
                  {link.name}
                  {location === link.path && (
                    <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[#D97736]" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Right Icons: Wishlist, Shopping Bag, User Account */}
            <div className="flex items-center space-x-3 sm:space-x-5">
              <Link
                href="/wishlist"
                className="cursor-pointer text-[#333333] hover:text-[#D97736] transition-colors p-1.5 hidden sm:block"
                aria-label="Wishlist"
              >
                <Heart className="w-5 h-5" />
              </Link>

              <button
                type="button"
                onClick={() => setIsCartOpen(true)}
                className="relative cursor-pointer text-[#333333] hover:text-[#D97736] transition-colors p-1.5"
                aria-label="Shopping Bag"
              >
                <ShoppingBag className="w-5 h-5 stroke-[1.8]" />
                {totalItems > 0 && (
                  <span className="absolute top-0 right-0 bg-[#D97736] text-white text-[9px] font-bold min-w-[17px] h-[17px] rounded-full flex items-center justify-center ring-2 ring-[#FAF7F2] shadow-sm">
                    {totalItems}
                  </span>
                )}
              </button>

              <div className="h-4 w-[1px] bg-[#E5E0D8] hidden sm:block"></div>

              {!loading && (
                user ? (
                  <div className="relative group p-1 hidden sm:block">
                    <img
                      src={user.photoURL || `https://ui-avatars.com/api/?name=${user.displayName || 'User'}&background=D97736&color=fff`}
                      alt="Profile"
                      referrerPolicy="no-referrer"
                      className="w-8 h-8 rounded-full border border-[#E5E0D8] group-hover:border-[#D97736] transition-colors"
                    />
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-[#E5E0D8] shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 rounded-lg overflow-hidden">
                      <div className="p-3 border-b border-[#E5E0D8] bg-[#FAF7F2]">
                        <p className="text-xs font-bold text-[#1F1F1F] truncate">{user.displayName || 'Customer'}</p>
                        <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
                      </div>
                      <Link href="/orders" className="block w-full text-left px-4 py-2.5 text-xs font-semibold text-[#333333] hover:bg-[#FAF7F2] hover:text-[#D97736] transition-colors">
                        My Orders
                      </Link>
                      <Link href="/account" className="block w-full text-left px-4 py-2.5 text-xs font-semibold text-[#333333] hover:bg-[#FAF7F2] hover:text-[#D97736] transition-colors">
                        My Account
                      </Link>
                      <button onClick={logout} className="w-full text-left px-4 py-2.5 text-xs font-bold text-[#D97736] hover:bg-[#FAF7F2] transition-colors border-t border-[#E5E0D8]">
                        Logout
                      </button>
                    </div>
                  </div>
                ) : (
                  <Link
                    href="/login"
                    className="text-xs font-bold uppercase tracking-wider text-[#1F1F1F] hover:text-white hover:bg-[#D97736] transition-colors py-1.5 px-3 border border-[#333333] hover:border-[#D97736] rounded cursor-pointer hidden sm:block"
                  >
                    Login
                  </Link>
                )
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        <AnimatePresence>
          {isOpen && (
            <div className="fixed inset-0 z-50 flex lg:hidden">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="fixed inset-0 bg-black/50 backdrop-blur-[2px] cursor-pointer"
              />

              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                className="relative w-full max-w-[300px] bg-[#FAF7F2] h-full z-50 flex flex-col shadow-2xl border-r border-[#E5E0D8]"
              >
                <div className="p-4 border-b border-[#E5E0D8] flex items-center justify-between bg-white">
                  <div>
                    <h3 className="font-serif font-bold text-lg text-[#1F1F1F] uppercase">Ajjas Masala</h3>
                    <p className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#C85A17]">The Joy of Good Taste</p>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-black transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto py-3">
                  <div className="px-2 space-y-0.5">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        href={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                          location === link.path
                            ? 'bg-[#2B2B2B] text-white'
                            : 'text-[#333333] hover:bg-[#EAE6DF] hover:text-black'
                        }`}
                      >
                        <span>{link.name}</span>
                        <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                      </Link>
                    ))}
                  </div>

                  <div className="h-[1px] bg-[#E5E0D8] my-3 mx-4" />

                  <div className="px-2 space-y-0.5">
                    <Link
                      href="/orders"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#333333] hover:bg-[#EAE6DF] rounded-lg transition-colors"
                    >
                      My Orders
                    </Link>
                    <Link
                      href="/wishlist"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#333333] hover:bg-[#EAE6DF] rounded-lg transition-colors"
                    >
                      Wishlist
                    </Link>
                    <Link
                      href="/account"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#333333] hover:bg-[#EAE6DF] rounded-lg transition-colors"
                    >
                      My Account
                    </Link>
                  </div>
                </div>

                <div className="p-4 border-t border-[#E5E0D8] bg-white">
                  {user ? (
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 pr-2">
                        <p className="text-xs font-bold text-[#1F1F1F] truncate">{user.displayName || 'Customer'}</p>
                        <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
                      </div>
                      <button
                        onClick={() => {
                          logout();
                          setIsOpen(false);
                        }}
                        className="text-xs font-bold text-[#D97736] hover:underline"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    <Link
                      href="/login"
                      onClick={() => setIsOpen(false)}
                      className="w-full py-2.5 px-4 btn-terracotta bg-[#D97736] text-white rounded font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-sm"
                    >
                      <LogIn className="w-4 h-4" />
                      <span>Customer Login</span>
                    </Link>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </header>

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
}
